import React, { useState, useEffect, useCallback } from 'react';
import Logo from './components/Logo';
import './KijijiTestPage.css';

const KijijiTestPage = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchCount, setFetchCount] = useState(0);
  const [responseData, setResponseData] = useState(null);
  
  // Facebook Marketplace state
  const [fbListings, setFbListings] = useState([]);
  const [fbLoading, setFbLoading] = useState(false);
  const [fbError, setFbError] = useState(null);
  const [fbFetchCount, setFbFetchCount] = useState(0);
  const [fbResponseData, setFbResponseData] = useState(null);
  
  // Scraping parameters
  const [maxPages, setMaxPages] = useState(3);
  const [maxListings, setMaxListings] = useState(25);

  const fetchKijijiListings = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Call our Lambda function's Kijiji test endpoint
      const url = `https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/kijiji-test?pages=${maxPages}&limit=${maxListings}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        if (data.success) {
          setListings(data.listings || []);
          setFetchCount(data.count || 0);
          setResponseData(data);
        } else {
          // Handle scraping failure case
          setListings([]);
          setFetchCount(0);
          setResponseData(data);
          setError(data.message || 'Failed to scrape listings from Kijiji');
        }
      } else {
        setError(`API Error (${response.status}): ${data.error || 'Failed to fetch listings'}`);
      }
    } catch (err) {
      setError(`Network error: ${err.message}. Check browser console for details.`);
      console.error('Kijiji Fetch Error:', err);
    } finally {
      setLoading(false);
    }
  }, [maxPages, maxListings]);

  // Facebook Marketplace property type categorization
  const categorizeFacebookListing = (listing) => {
    const title = (listing.marketplace_listing_title || '').toLowerCase();
    const customTitle = (listing.custom_title || '').toLowerCase();
    const price = listing.listing_price?.amount ? parseFloat(listing.listing_price.amount) : 0;
    const location = listing.location?.reverse_geocode?.city || '';
    
    // Rule 1: If price is less than $200, ignore the listing
    if (price < 200) {
      return { shouldIgnore: true, propertyType: null };
    }

    // Rule 2: If you found "private room for rent" in title, then property type = room
    if (title.includes('private room for rent')) {
      return { shouldIgnore: false, propertyType: 'Room' };
    }

    // Rule 3: If word basement is in title, then property type = basement
    if (title.includes('basement')) {
      return { shouldIgnore: false, propertyType: 'Basement' };
    }

    // Rule 4: If price is less than $1000, property type = room
    if (price < 1000) {
      return { shouldIgnore: false, propertyType: 'Room' };
    }

    // Rule 5: If the title says apartment, townhouse or house and location is Toronto but price is less than $2000, then property type = basement
    const isTorontoLocation = location.toLowerCase().includes('toronto');
    const hasPropertyKeyword = title.includes('apartment') || title.includes('townhouse') || title.includes('house') ||
                              customTitle.includes('apartment') || customTitle.includes('townhouse') || customTitle.includes('house');
    
    if (hasPropertyKeyword && isTorontoLocation && price < 2000) {
      return { shouldIgnore: false, propertyType: 'Basement' };
    }

    // Rule 6: Else: use title and description to identify the property type
    const fullText = `${title} ${customTitle}`.toLowerCase();
    
    // Check for specific keywords in order of specificity
    if (fullText.includes('room') || fullText.includes('private') || fullText.includes('shared')) {
      return { shouldIgnore: false, propertyType: 'Room' };
    }
    
    if (fullText.includes('basement') || fullText.includes('lower level') || fullText.includes('ground floor')) {
      return { shouldIgnore: false, propertyType: 'Basement' };
    }
    
    if (fullText.includes('townhouse') || fullText.includes('town house') || fullText.includes('row house')) {
      return { shouldIgnore: false, propertyType: 'Townhouse' };
    }
    
    if (fullText.includes('house') || fullText.includes('detached') || fullText.includes('single family')) {
      return { shouldIgnore: false, propertyType: 'House' };
    }
    
    if (fullText.includes('apartment') || fullText.includes('condo') || fullText.includes('unit') || 
        fullText.includes('studio') || fullText.includes('1 bed') || fullText.includes('2 bed') || 
        fullText.includes('3 bed') || fullText.includes('bachelor')) {
      return { shouldIgnore: false, propertyType: 'Apartment' };
    }

    // Default to Apartment if no specific type is detected
    return { shouldIgnore: false, propertyType: 'Apartment' };
  };

  const fetchFacebookListings = useCallback(async () => {
    setFbLoading(true);
    setFbError(null);
    
    try {
      const url = 'https://api.apify.com/v2/acts/apify~facebook-marketplace-scraper/runs/last/dataset/items?token=apify_api_bmdnqEKQbxyZINn7p6S2Ya9JUMxjFH1BRna1';
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        // Filter out invalid listings and categorize them
        const processedListings = data
          .filter(listing => 
            listing && 
            listing.id && 
            listing.marketplace_listing_title &&
            !listing.is_sold &&
            listing.is_live
          )
          .map(listing => {
            const categorization = categorizeFacebookListing(listing);
            return {
              ...listing,
              propertyType: categorization.propertyType,
              shouldIgnore: categorization.shouldIgnore
            };
          })
          .filter(listing => !listing.shouldIgnore); // Remove listings that should be ignored
        
        setFbListings(processedListings);
        setFbFetchCount(processedListings.length);
        setFbResponseData({
          success: true,
          listings: processedListings,
          count: processedListings.length,
          source: 'Facebook Marketplace (Apify)',
          timestamp: new Date().toISOString(),
          total_scraped: data.length,
          valid_listings: processedListings.length,
          categorization_applied: true,
          price_filter_applied: 'Listings under $200 excluded'
        });
      } else {
        setFbError(`API Error (${response.status}): Failed to fetch Facebook Marketplace listings`);
      }
    } catch (err) {
      setFbError(`Network error: ${err.message}. Check browser console for details.`);
      console.error('Facebook Marketplace Fetch Error:', err);
    } finally {
      setFbLoading(false);
    }
  }, []);

  useEffect(() => {
    // Auto-fetch listings when component mounts
    fetchKijijiListings();
    fetchFacebookListings();
  }, [fetchKijijiListings, fetchFacebookListings]);

  const formatPrice = (price) => {
    if (!price || price === 'N/A') return 'Price not available';
    return price;
  };

  const formatFacebookPrice = (priceObj) => {
    if (!priceObj || !priceObj.formatted_amount) return 'Price not available';
    return priceObj.formatted_amount;
  };

  const formatJSON = (obj) => {
    // Convert object to formatted JSON string
    const jsonString = JSON.stringify(obj, null, 2);
    
    // Apply syntax highlighting with HTML spans
    return jsonString
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/(".*?")\s*:/g, '<span style="color: #4FC3F7;">$1</span>:') // Keys in blue
      .replace(/:\s*(".*?")/g, ': <span style="color: #A5D6A7;">$1</span>') // String values in green
      .replace(/:\s*(\d+\.?\d*)/g, ': <span style="color: #FFCC80;">$1</span>') // Numbers in orange
      .replace(/:\s*(true|false)/g, ': <span style="color: #F48FB1;">$1</span>') // Booleans in pink
      .replace(/:\s*(null)/g, ': <span style="color: #B0BEC5;">$1</span>') // null in grey
      .replace(/([{}[\]])/g, '<span style="color: #FFE082;">$1</span>') // Brackets in yellow
      .replace(/(,)$/gm, '<span style="color: #E0E0E0;">$1</span>'); // Commas in light grey
  };

  const PhotoGallery = ({ photos, listingId }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

    if (!photos || photos.length === 0) {
      return (
        <div className="no-photos">
          <div className="no-photos-placeholder">
            🏠 No photos available
          </div>
        </div>
      );
    }

    const mainPhoto = photos[selectedPhotoIndex] || photos[0];

    return (
      <div className="photo-gallery">
        <div className="main-photo-container">
          <div className="main-photo">
            {!imageLoaded && <div className="photo-loading">Loading...</div>}
            <img 
              src={mainPhoto.url} 
              alt={mainPhoto.description || 'Property photo'}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                e.target.style.display = 'none';
                setImageLoaded(true);
              }}
              style={{ display: imageLoaded ? 'block' : 'none' }}
            />
            
            {photos.length > 1 && (
              <div className="photo-count-badge">
                📷 {photos.length} photos
              </div>
            )}
          </div>

          {mainPhoto.description && (
            <div className="photo-description">
              {mainPhoto.description}
            </div>
          )}
        </div>
        
        {/* Thumbnail strip for multiple photos */}
        {photos.length > 1 && (
          <div className="thumbnail-strip">
            {photos.slice(0, 5).map((photo, index) => (
              <div 
                key={index}
                className={`thumbnail ${index === selectedPhotoIndex ? 'active' : ''}`}
                onClick={() => {
                  setSelectedPhotoIndex(index);
                  setImageLoaded(false);
                }}
              >
                <img 
                  src={photo.url} 
                  alt={photo.description || `Photo ${index + 1}`}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/80x60/cccccc/666666?text=No+Image';
                  }}
                />
              </div>
            ))}
            {photos.length > 5 && (
              <div className="more-photos-indicator">
                +{photos.length - 5} more
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const FacebookPhotoDisplay = ({ listing }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    
    if (!listing.primary_listing_photo || !listing.primary_listing_photo.photo_image_url) {
      return (
        <div className="no-photos">
          <div className="no-photos-placeholder">
            🏠 No photos available
          </div>
        </div>
      );
    }

    return (
      <div className="photo-gallery facebook-photo">
        <div className="main-photo-container">
          <div className="main-photo">
            {!imageLoaded && <div className="photo-loading">Loading...</div>}
            <img 
              src={listing.primary_listing_photo.photo_image_url} 
              alt={listing.marketplace_listing_title || 'Property photo'}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                e.target.style.display = 'none';
                setImageLoaded(true);
              }}
              style={{ display: imageLoaded ? 'block' : 'none' }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="kijiji-test-page">
      {/* Header */}
      <header className="kijiji-header">
        <div className="header-container">
          <Logo size="medium" showText={true} />
          <div className="header-title">
            <h1>Rental Listing Scrapers Test</h1>
            <p>Testing Kijiji & Facebook Marketplace Web Scraping for Rental Properties</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="kijiji-main">
        <div className="container">
          {/* Facebook Marketplace Section */}
          <section className="facebook-section">
            <div className="section-header">
              <h2>🟦 Facebook Marketplace - Toronto Rentals</h2>
              <p>Real-time data from Facebook Marketplace via Apify scraper</p>
            </div>

            <div className="facebook-controls">
              <button 
                onClick={fetchFacebookListings} 
                disabled={fbLoading}
                className="fetch-button facebook-button"
              >
                {fbLoading ? 'Loading...' : '🔄 Refresh Facebook Listings'}
              </button>
              
              {fbFetchCount > 0 && (
                <div className="fetch-info facebook-info">
                  Found {fbFetchCount} active rental listings from Facebook Marketplace
                  <br />
                  <small style={{color: '#888', fontStyle: 'italic'}}>
                    Scraped via Apify Facebook Marketplace scraper
                  </small>
                </div>
              )}
            </div>

            {/* Facebook Raw API Response */}
            {fbResponseData && (
              <div className="api-response-section facebook-response">
                <h3>📊 Raw Facebook Marketplace Response</h3>
                <p>Complete JSON response from the Apify Facebook Marketplace scraper:</p>
                
                <div className="json-response">
                  <div className="json-header">
                    <h5>📋 Apify Response Summary:</h5>
                    <button 
                      className="copy-button"
                      onClick={(event) => {
                        navigator.clipboard.writeText(JSON.stringify(fbResponseData, null, 2));
                        const button = event.target;
                        const originalText = button.textContent;
                        button.textContent = '✅ Copied!';
                        setTimeout(() => {
                          button.textContent = originalText;
                        }, 2000);
                      }}
                    >
                      📋 Copy Response JSON
                    </button>
                  </div>
                  
                  <div className="response-summary">
                    <div className="summary-grid">
                      <div className="summary-item">
                        <strong>Valid Listings:</strong> {fbResponseData.valid_listings || 0}
                      </div>
                      <div className="summary-item">
                        <strong>Total Scraped:</strong> {fbResponseData.total_scraped || 0}
                      </div>
                      <div className="summary-item">
                        <strong>Data Source:</strong> {fbResponseData.source || 'Facebook Marketplace'}
                      </div>
                      <div className="summary-item">
                        <strong>Timestamp:</strong> {fbResponseData.timestamp ? new Date(fbResponseData.timestamp).toLocaleString() : 'Unknown'}
                      </div>
                    </div>

                    {/* Property Type Breakdown */}
                    {fbListings.length > 0 && (
                      <div className="property-breakdown">
                        <h5>🏠 Property Type Breakdown:</h5>
                        <div className="breakdown-grid">
                          {(() => {
                            const typeCounts = fbListings.reduce((acc, listing) => {
                              const type = listing.propertyType || 'Unknown';
                              acc[type] = (acc[type] || 0) + 1;
                              return acc;
                            }, {});
                            
                            return Object.entries(typeCounts).map(([type, count]) => (
                              <div key={type} className="breakdown-item">
                                <span className="breakdown-type">{type}:</span>
                                <span className="breakdown-count">{count} listings</span>
                              </div>
                            ));
                          })()}
                        </div>
                        
                        <div className="categorization-rules">
                          <h6>📋 Categorization Rules Applied:</h6>
                          <ul>
                            <li>Listings under $200 excluded</li>
                            <li>"Private room for rent" → Room</li>
                            <li>"Basement" in title → Basement</li>
                            <li>Price under $1,000 → Room</li>
                            <li>Toronto + Apartment/House/Townhouse + Under $2,000 → Basement</li>
                            <li>Keyword analysis for remaining listings</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Facebook Error Display */}
            {fbError && (
              <div className="error-message facebook-error">
                <h3>⚠️ Facebook Scraping Failed</h3>
                <p><strong>{fbError}</strong></p>
                <button onClick={fetchFacebookListings} className="retry-button">
                  Try Again
                </button>
              </div>
            )}

            {/* Facebook Loading State */}
            {fbLoading && (
              <div className="loading-state facebook-loading">
                <div className="loading-spinner"></div>
                <p>Loading Facebook Marketplace listings...</p>
                <small>Fetching latest data from Apify scraper</small>
              </div>
            )}

            {/* Facebook Listings Grid */}
            {!fbLoading && !fbError && fbListings.length > 0 && (
              <div className="listings-grid facebook-grid">
                {fbListings.map((listing, index) => (
                  <div key={listing.id || index} className="listing-card facebook-card">
                    <FacebookPhotoDisplay listing={listing} />
                    
                    <div className="listing-header">
                      <h3>{listing.marketplace_listing_title || 'Title not available'}</h3>
                      <div className="listing-price facebook-price">
                        {formatFacebookPrice(listing.listing_price)}
                      </div>
                      {listing.propertyType && (
                        <div className="property-type-badge facebook-property-type">
                          🏠 {listing.propertyType}
                        </div>
                      )}
                    </div>

                    <div className="listing-info">
                      <div className="listing-details">
                        {listing.location && listing.location.reverse_geocode && (
                          <div className="detail-item">
                            <span className="label">📍 Location:</span>
                            <span className="value">
                              {listing.location.reverse_geocode.city}, {listing.location.reverse_geocode.state}
                            </span>
                          </div>
                        )}
                        
                        {listing.custom_title && (
                          <div className="property-specs">
                            <span className="spec">🏠 {listing.custom_title}</span>
                          </div>
                        )}

                        {listing.custom_sub_titles_with_rendering_flags && listing.custom_sub_titles_with_rendering_flags.length > 0 && (
                          <div className="address-info">
                            {listing.custom_sub_titles_with_rendering_flags.map((subtitle, idx) => (
                              <div key={idx} className="address-line">
                                📍 {subtitle.subtitle}
                              </div>
                            ))}
                          </div>
                        )}

                        {listing.propertyType && (
                          <div className="categorization-info">
                            <span className="label">🏷️ Auto-categorized as:</span>
                            <span className="value property-type-value">{listing.propertyType}</span>
                          </div>
                        )}
                      </div>

                      <div className="listing-meta">
                        <div className="meta-item">
                          <span className="label">💰 Price:</span>
                          <span className="value">{formatFacebookPrice(listing.listing_price)}</span>
                        </div>
                        
                        {listing.listingUrl && (
                          <div className="meta-item">
                            <span className="label">🔗 Link:</span>
                            <span className="value">
                              <a href={listing.listingUrl} target="_blank" rel="noopener noreferrer">
                                View on Facebook
                              </a>
                            </span>
                          </div>
                        )}
                        
                        <div className="meta-item source-item">
                          <span className="label">🏷️ Source:</span>
                          <span className="value source-value facebook-source">
                            🟦 Facebook Marketplace
                          </span>
                        </div>

                        <div className="meta-item">
                          <span className="label">🏷️ Category:</span>
                          <span className="value">
                            {listing.marketplace_listing_category_id === "1468271819871448" ? "For Rent" : "Property"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No Facebook Listings */}
            {!fbLoading && !fbError && fbListings.length === 0 && (
              <div className="no-listings facebook-no-listings">
                <h3>No Facebook listings found</h3>
                <p>No rental listings were found from Facebook Marketplace. This could be due to:</p>
                <ul>
                  <li>No recent scraping runs available</li>
                  <li>API rate limiting</li>
                  <li>All listings are sold/inactive</li>
                  <li>Scraper configuration issues</li>
                </ul>
              </div>
            )}
          </section>

          {/* Divider */}
          <div className="section-divider"></div>

          {/* Kijiji Section */}
          <section className="kijiji-section">
            <div className="section-header">
              <h2>🟠 Kijiji - Toronto Rental Listings</h2>
              <p>Testing Kijiji Web Scraping for Rental Properties</p>
            </div>

            {/* Controls */}
            <div className="kijiji-controls">
              <div className="control-group">
                <label htmlFor="maxPages">Pages to scrape:</label>
                <select 
                  id="maxPages"
                  value={maxPages} 
                  onChange={(e) => setMaxPages(parseInt(e.target.value))}
                  disabled={loading}
                >
                  <option value={1}>1 page</option>
                  <option value={2}>2 pages</option>
                  <option value={3}>3 pages</option>
                  <option value={4}>4 pages</option>
                  <option value={5}>5 pages</option>
                </select>
              </div>
              
              <div className="control-group">
                <label htmlFor="maxListings">Max listings:</label>
                <select 
                  id="maxListings"
                  value={maxListings} 
                  onChange={(e) => setMaxListings(parseInt(e.target.value))}
                  disabled={loading}
                >
                  <option value={10}>10 listings</option>
                  <option value={15}>15 listings</option>
                  <option value={20}>20 listings</option>
                  <option value={25}>25 listings</option>
                  <option value={30}>30 listings</option>
                  <option value={50}>50 listings</option>
                </select>
              </div>
              
              <button 
                onClick={fetchKijijiListings} 
                disabled={loading}
                className="fetch-button"
              >
                {loading ? 'Scraping...' : 'Scrape Listings'}
              </button>
              
              {fetchCount > 0 && (
                <div className="fetch-info">
                  Found {fetchCount} rental listings from Kijiji
                  <br />
                  <small style={{color: '#888', fontStyle: 'italic'}}>
                    {responseData?.scraping_info ? 
                      `Scraped ${responseData.scraping_info.pages_scraped} pages` : 
                      'Real Kijiji data scraped from Toronto apartments section'
                    }
                  </small>
                </div>
              )}
            </div>

            {/* Raw API Response Section */}
            {responseData && (
              <div className="api-response-section">
                <h3>📊 Raw Kijiji Scraping Response</h3>
                <p>Complete JSON response from the Kijiji web scraping endpoint:</p>
                
                <div className="json-response">
                  <div className="json-header">
                    <h5>📋 Complete JSON Response:</h5>
                    <button 
                      className="copy-button"
                      onClick={(event) => {
                        navigator.clipboard.writeText(JSON.stringify(responseData, null, 2));
                        // Show a brief feedback
                        const button = event.target;
                        const originalText = button.textContent;
                        button.textContent = '✅ Copied!';
                        setTimeout(() => {
                          button.textContent = originalText;
                        }, 2000);
                      }}
                    >
                      📋 Copy Full Response JSON
                    </button>
                  </div>
                  <div className="json-container">
                    <pre dangerouslySetInnerHTML={{
                      __html: formatJSON(responseData)
                    }}></pre>
                  </div>
                </div>

                <div className="response-summary">
                  <h5>📈 Scraping Summary:</h5>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <strong>Total Listings:</strong> {responseData.count || 0}
                    </div>
                    <div className="summary-item">
                      <strong>Data Source:</strong> {responseData.source || 'Kijiji'}
                    </div>
                    <div className="summary-item">
                      <strong>Pages Scraped:</strong> {responseData.scraping_info?.pages_scraped || 'Unknown'}
                    </div>
                    <div className="summary-item">
                      <strong>Timestamp:</strong> {responseData.timestamp ? new Date(responseData.timestamp).toLocaleString() : 'Unknown'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="error-message">
                <h3>⚠️ Scraping Failed</h3>
                <p><strong>{error}</strong></p>
                
                {responseData && responseData.error_details && (
                  <div className="error-details">
                    <p>{responseData.error_details}</p>
                    
                    {responseData.attempted_url && (
                      <div className="attempted-url">
                        <strong>Attempted URL:</strong> 
                        <a href={responseData.attempted_url} target="_blank" rel="noopener noreferrer">
                          {responseData.attempted_url}
                        </a>
                      </div>
                    )}
                    
                    {responseData.scraping_info && (
                      <div className="scraping-details">
                        <p><strong>Scraping Details:</strong></p>
                        <ul>
                          <li>Pages attempted: {responseData.scraping_info.pages_attempted}</li>
                          <li>Max listings requested: {responseData.scraping_info.max_listings_requested}</li>
                          <li>Listings found: {responseData.scraping_info.actual_listings_found}</li>
                          <li>Reason: {responseData.scraping_info.reason}</li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="error-suggestions">
                  <p><strong>Possible reasons:</strong></p>
                  <ul>
                    <li>Kijiji has anti-scraping measures in place</li>
                    <li>Website structure has changed</li>
                    <li>Network connectivity issues</li>
                    <li>Rate limiting or IP blocking</li>
                    <li>No listings currently available in Toronto</li>
                  </ul>
                </div>
                
                <button onClick={fetchKijijiListings} className="retry-button">
                  Try Again
                </button>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Scraping Kijiji for Toronto rental listings...</p>
                <small>This may take 30-60 seconds depending on the number of pages</small>
              </div>
            )}

            {/* Listings Grid */}
            {!loading && !error && listings.length > 0 && (
              <div className="listings-grid">
                {listings.map((listing, index) => (
                  <div key={listing.id || index} className="listing-card">
                    <PhotoGallery photos={listing.photos} listingId={listing.id} />
                    
                    <div className="listing-header">
                      <h3>{listing.title || 'Title not available'}</h3>
                      <div className="listing-price">
                        {formatPrice(listing.price)}
                      </div>
                    </div>

                    <div className="listing-info">
                      <div className="listing-details">
                        {listing.location && (
                          <div className="detail-item">
                            <span className="label">📍 Location:</span>
                            <span className="value">{listing.location}</span>
                          </div>
                        )}
                        
                        {(listing.bedrooms || listing.bathrooms || listing.sqft) && (
                          <div className="property-specs">
                            {listing.bedrooms && (
                              <span className="spec">🛏️ {listing.bedrooms} bed{listing.bedrooms !== 1 ? 's' : ''}</span>
                            )}
                            {listing.bathrooms && (
                              <span className="spec">🚿 {listing.bathrooms} bath{listing.bathrooms !== 1 ? 's' : ''}</span>
                            )}
                            {listing.sqft && (
                              <span className="spec">📐 {listing.sqft} sqft</span>
                            )}
                          </div>
                        )}

                        {listing.description && listing.description !== 'N/A' && (
                          <div className="description">
                            <span className="label">📝 Description:</span>
                            <span className="value">{listing.description}</span>
                          </div>
                        )}
                      </div>

                      <div className="listing-meta">
                        {listing.date_posted && (
                          <div className="meta-item">
                            <span className="label">📅 Posted:</span>
                            <span className="value">{listing.date_posted}</span>
                          </div>
                        )}
                        
                        {listing.link && listing.link !== 'N/A' && (
                          <div className="meta-item">
                            <span className="label">🔗 Link:</span>
                            <span className="value">
                              <a href={listing.link} target="_blank" rel="noopener noreferrer">
                                View on Kijiji
                              </a>
                            </span>
                          </div>
                        )}
                        
                        <div className="meta-item source-item">
                          <span className="label">🏷️ Source:</span>
                          <span className="value source-value">
                            <img 
                              src="/kijiji-icon-filled-128.webp" 
                              alt="Kijiji" 
                              className="source-logo"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            {listing.source}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No Listings */}
            {!loading && !error && listings.length === 0 && (
              <div className="no-listings">
                <h3>No listings found</h3>
                <p>No rental listings were found on Kijiji. This could be due to:</p>
                <ul>
                  <li>Network issues or request blocking</li>
                  <li>Changes in Kijiji's website structure</li>
                  <li>No listings currently available in Toronto</li>
                  <li>Temporary scraping restrictions</li>
                </ul>
              </div>
            )}
          </section>

          {/* Technical Info */}
          <div className="technical-info">
            <h4>Technical Details:</h4>
            <div className="tech-details">
              <div><strong>Kijiji Source:</strong> Kijiji.ca - Toronto Apartments & Condos</div>
              <div><strong>Facebook Source:</strong> Facebook Marketplace via Apify scraper</div>
              <div><strong>Kijiji Method:</strong> BeautifulSoup + requests</div>
              <div><strong>Facebook Method:</strong> Apify automated scraper</div>
              <div><strong>Lambda Function:</strong> tenanthelpai-function</div>
              <div><strong>Last Updated:</strong> {new Date().toLocaleString()}</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default KijijiTestPage; 